// src/components/events/SimpleFormDesigner.jsx
import React, { useMemo, useState } from 'react';
import SimpleFieldEditor from './SimpleFieldEditor';
import FormPreview from './FormPreview';

function normalizeName(s) {
  return (s || '')
    .toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
    .slice(0, 48);
}

const PALETTE = [
  { type: 'text',           title: 'Textfeld' },
  { type: 'email',          title: 'E-Mail' },
  { type: 'phone',          title: 'Telefon' },             // <— neu
  { type: 'textarea',       title: 'Mehrzeiliger Text' },
  { type: 'radio',          title: 'Einfachauswahl (Radio)' },
  { type: 'select',         title: 'Einfachauswahl (Select)' },
  { type: 'checkbox',       title: 'Checkbox (einzeln)' },
  { type: 'checkbox_group', title: 'Mehrfachauswahl (Checkbox-Gruppe)' },
  { type: 'consent',        title: 'Einwilligung (Consent)' },
];

function makeDefaultField(type) {
  switch (type) {
    case 'text':
      return { type, name: 'text', label: 'Text', required: false, placeholder: '' };
    case 'email':
      return { type, name: 'email', label: 'E-Mail', required: true, placeholder: '' };
    case 'phone':
      return { type, name: 'phone', label: 'Telefon', required: false, placeholder: '', pattern: '' };
    case 'textarea':
      return { type, name: 'message', label: 'Nachricht', required: false, rows: 3, placeholder: '' };
    case 'radio':
      return {
        type, name: 'choice', label: 'Auswahl', required: false,
        options: [{ value: 'a', label: 'Option A' }, { value: 'b', label: 'Option B' }]
      };
    case 'select':
      return {
        type, name: 'select', label: 'Bitte wählen', required: false,
        options: [{ value: 'a', label: 'Option A' }, { value: 'b', label: 'Option B' }]
      };
    case 'checkbox':
      return { type, name: 'agree', label: 'Ich stimme zu', required: false };
    case 'checkbox_group':
      return {
        type, name: 'multi', label: 'Mehrfachauswahl', required: false,
        options: [{ value: 'a', label: 'Option A' }, { value: 'b', label: 'Option B' }]
      };
    case 'consent':
      return {
        type, name: 'consent',
        html: 'Ich habe die <a href="https://digitecstudieren.de/datenschutz.html" target="_blank" rel="noopener">Datenschutzerklärung</a> zur Kenntnis genommen.',
        required: true
      };
    default:
      return { type, name: type, label: type };
  }
}

export default function SimpleFormDesigner({ value, onChange }) {
  const schema = value && typeof value === 'object' ? value : { fields: [] };
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [picker, setPicker] = useState('text');

  const fields = useMemo(() => Array.isArray(schema.fields) ? schema.fields : [], [schema]);
  const selected = fields[selectedIdx] || null;

  const setFields = (next) => onChange?.({ ...schema, fields: next });

  const addField = (type) => {
    const base = makeDefaultField(type);
    // automatisch Name aus Label ziehen und einzigartig machen
    const baseName = normalizeName(base.label || base.name || type) || type;
    let name = baseName;
    let ct = 1;
    const used = new Set(fields.map(f => f.name));
    while (used.has(name)) { name = `${baseName}_${++ct}`; }
    const field = { ...base, name };
    setFields([ ...fields, field ]);
    setSelectedIdx(fields.length);
  };

  const removeField = (i) => {
    const next = [ ...fields ];
    next.splice(i, 1);
    setFields(next);
    setSelectedIdx(Math.max(0, i - 1));
  };

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= fields.length) return;
    const next = [ ...fields ];
    const tmp = next[i];
    next[i] = next[j];
    next[j] = tmp;
    setFields(next);
    setSelectedIdx(j);
  };

  const updateField = (i, patch) => {
    if (!fields[i]) return;
    // Duplizieren aus dem Editor heraus
    if (patch && patch.__duplicate) {
      const clone = { ...fields[i] };
      delete clone.id;
      const baseName = normalizeName((clone.label || clone.name) + '_copy') || 'feld';
      let name = baseName; let k = 1;
      const used = new Set(fields.map(f => f.name));
      while (used.has(name)) name = `${baseName}_${++k}`;
      clone.name = name;
      const next = [ ...fields ];
      next.splice(i + 1, 0, clone);
      setFields(next);
      setSelectedIdx(i + 1);
      return;
    }
    const next = [ ...fields ];
    next[i] = { ...next[i], ...patch };
    // wenn Label geändert und noch kein eigener Name gesetzt war
    if ('label' in patch && (!fields[i].name || fields[i].name === normalizeName(fields[i].label || ''))) {
      next[i].name = normalizeName(patch.label);
    }
    setFields(next);
  };

  return (
    <div className="grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 360px) 1fr', gap: '1rem' }}>
      {/* Linke Spalte: Palette & Liste */}
      <div>
        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'end' }}>
            <div>
              <label>Feld hinzufügen</label>
              <select value={picker} onChange={e => setPicker(e.target.value)}>
                {PALETTE.map(p => <option key={p.type} value={p.type}>{p.title}</option>)}
              </select>
            </div>
            <button className="primary" onClick={() => addField(picker)}>Hinzufügen</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6, marginTop: 10 }}>
            {PALETTE.map(p => (
              <button key={p.type} className="secondary" onClick={() => addField(p.type)}>{p.title}</button>
            ))}
          </div>
        </div>

        <div className="card">
          <h5 style={{ marginTop: 0 }}>Felder</h5>
          <div style={{ display: 'grid', gap: 6 }}>
            {fields.map((f, i) => (
              <div
                key={f.name || i}
                className={'card'}
                onClick={() => setSelectedIdx(i)}
                style={{
                  padding: 8,
                  borderColor: i === selectedIdx ? '#06A69E' : '#eee',
                  cursor: 'pointer',
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  gap: 8
                }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>{f.label || f.name || f.type}</div>
                  <div style={{ fontSize: 12, opacity: .7 }}>{f.type}{f.required ? ' • Pflichtfeld' : ''}</div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <button className="link" title="nach oben" onClick={(e) => { e.stopPropagation(); move(i, -1); }}>↑</button>
                  <button className="link" title="nach unten" onClick={(e) => { e.stopPropagation(); move(i, +1); }}>↓</button>
                  <button className="link" title="löschen" onClick={(e) => { e.stopPropagation(); removeField(i); }}>🗑</button>
                </div>
              </div>
            ))}
            {fields.length === 0 && <div className="muted">Noch keine Felder.</div>}
          </div>
        </div>
      </div>

      {/* Rechte Spalte: Editor & Vorschau */}
      <div style={{ display: 'grid', gap: '1rem' }}>
        <div className="card">
          <h5 style={{ marginTop: 0 }}>Feld bearbeiten</h5>
          {selected ? (
            <SimpleFieldEditor
              field={selected}
              onChange={(patch) => updateField(selectedIdx, patch)}
            />
          ) : (
            <div className="muted">Bitte ein Feld links auswählen.</div>
          )}
        </div>

        <div className="card">
          <h5 style={{ marginTop: 0 }}>Vorschau</h5>
          <FormPreview fields={fields} />
        </div>
      </div>
    </div>
  );
}